/**
 * `FRENCH-SPACING` (the architect's ruling of 23 October 2026): **server sentences carry French typography at source, display only.**
 *
 * - before « : ; ? ! » and before « » », and after « « »: the narrow no-break space, U+202F;
 * - before « FCFA »: the no-break space, U+00A0 (06 §6.5.2 and `DECISION ADDED 06-35`, amended by `MONEY-SPACE`);
 * - before « % »: `PERCENT_GAP`, ONE constant, the narrow no-break space U+202F (D-1025-AA-1);
 *
 * **Only a space that is THERE is converted.** None is added (a time, `12:30`, a scheme, `https://`, stay as they are), and a second
 * pass changes nothing, so a builder may apply it to its own output without asking whether a caller already did. The group
 * separators inside a number are not this function's: they are `MONEY-GROUPING`'s.
 *
 * It is applied at the exits of the builders that make a displayed sentence (the counters' reasons, the grade's operands and notes,
 * the term note, the composer's details), never to a typed figure, an operand object or a code, and never to what a model wrote.
 * A prompt is not a display: the prompt boundary (`prompt-figures.ts`) gives back their plain U+0020 to what this function adds
 * before « : ; ? ! », around the guillemets and before « % » (every server « % » producer was U+0020 on main). The gap before
 * « FCFA » is the canon's and the model reads it as written (the one model-visible change, declared by record).
 */
/** The narrow no-break space, U+202F: before « : ; ? ! » and « » », after « « », and inside a number between its groups (`06-16`). */
export const NARROW_NO_BREAK_SPACE = "\u202f";
/** The no-break space, U+00A0. */
export const NO_BREAK_SPACE = "\u00a0";
/** The gap between the digit groups of a number: U+202F (`06-16`, `MONEY-GROUPING`). */
export const GROUP_GAP = NARROW_NO_BREAK_SPACE;
/** The gap before « FCFA »: U+00A0 (`06-35`, amended by record, `MONEY-SPACE`). */
export const FCFA_GAP = NO_BREAK_SPACE;
/** The gap before « % »: the narrow no-break space (the founder's delegated call, D-1025-AA-1). One constant: the width is a one-line change. */
export const PERCENT_GAP = NARROW_NO_BREAK_SPACE;

export function frenchTypography(text: string): string {
  return text
    .replaceAll(/ (?=[:;?!»])/gu, NARROW_NO_BREAK_SPACE)
    .replaceAll(/« /gu, `«${NARROW_NO_BREAK_SPACE}`)
    .replaceAll(/ (?=%)/gu, PERCENT_GAP)
    .replaceAll(/ (?=FCFA(?![\p{L}\d]))/gu, FCFA_GAP);
}
